/**
 * Compound font-value helpers for the variant grid.
 *
 * A font-family cell stores family, weight and style as a single string:
 *   "Roboto||700||italic"
 * Plain "Roboto" is valid too — defaults to weight 400 / style normal.
 */
import { CDN_FONT_NAMES, SYSTEM_FONT_NAMES } from '@/constants/fonts';
import { variantLabel, toVariantKey } from '@/helpers/fontHelper';
import { useFontStore } from '@/stores/fontStore';
import type { FontFamilyAttributeValue } from '@/types/library/font';
export type { FontFamilyAttributeValue } from '@/types/library/font';

const SEPARATOR = '||';
const DEFAULT_WEIGHT = 400;
const DEFAULT_STYLE = 'normal';

export function composeFontValue(
  family: string,
  weight: number,
  style: string
): string {
  if (weight === DEFAULT_WEIGHT && style === DEFAULT_STYLE) return family;
  return `${family}${SEPARATOR}${weight}${SEPARATOR}${style}`;
}

export function parseFontValue(raw: string): FontFamilyAttributeValue {
  const parts = raw.split(SEPARATOR);
  if (parts.length >= 3) {
    return {
      family: parts[0]!,
      weight: Number(parts[1]) || DEFAULT_WEIGHT,
      style: parts[2] === 'italic' ? 'italic' : DEFAULT_STYLE
    };
  }
  return { family: raw, weight: DEFAULT_WEIGHT, style: DEFAULT_STYLE };
}

export function formatFontLabel(raw: string): string {
  const { family, weight, style } = parseFontValue(raw);
  const variantKey = toVariantKey(weight, style);
  if (variantKey === 'regular') return family;
  return `${family} ${variantLabel(variantKey)}`;
}

export function isKnownFontFamily(family: string): boolean {
  if (!family) return false;
  const lower = family.toLowerCase();

  if (SYSTEM_FONT_NAMES.some(n => n.toLowerCase() === lower)) return true;
  if (CDN_FONT_NAMES.some(n => n.toLowerCase() === lower)) return true;

  const fontStore = useFontStore();
  if (fontStore.allGoogleFonts.has(family)) return true;
  if (fontStore.uploadedFonts.has(family)) return true;

  return false;
}

export function isFontRegistryEmpty(): boolean {
  const fontStore = useFontStore();
  return !fontStore.isInitialized && fontStore.allGoogleFonts.size === 0;
}

const fontRegistryListeners: (() => void)[] = [];

export function onFontRegistryChange(cb: () => void): () => void {
  fontRegistryListeners.push(cb);
  return () => {
    const idx = fontRegistryListeners.indexOf(cb);
    if (idx !== -1) fontRegistryListeners.splice(idx, 1);
  };
}

export function notifyFontRegistryChange(): void {
  for (const cb of fontRegistryListeners) cb();
}

let defaultFontProvider: (() => string | null) | null = null;

export function registerDefaultFontProvider(fn: () => string | null): void {
  defaultFontProvider = fn;
}

export function getDefaultFontFamily(): string | null {
  return defaultFontProvider?.() ?? null;
}
